import { LTime } from '@app/components/date/LTime';
import { isDiarizationSegmentsEnabled } from '@app/modules/logger/screens/stream/diarization/isDiarizationSegmentsEnabled';
import { theme } from '@mi-front/ui';
import { MrtTable, NullState, SwitchInput } from '@ui/components';
import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { DiarizationContext } from '../../../diarization/DiarizationContext';
import { LabelWrapper, SwitchWrapper } from './styles';
import { FormattedTranscriptionType, TranscriptionType } from './transcriptions.types';
import TrascriptionsLabel from './TranscriptionsLabel';
import TranscriptionSpeakersCell from './TranscriptionSpeakersCell';
import TranscriptionTranscriptCell from './TranscriptionTranscriptCell';
import { filteredTranscript, formatTranscript, parseTranscription } from './utils';
import { Station } from '@app/core/types/stations';

interface ITranscriptionsView {
  data: TranscriptionType[][];
  loading: boolean;
  station: Station;
}

const TranscriptionsView: React.FunctionComponent<ITranscriptionsView> = memo((props) => {
  const { data, loading, station } = props;
  const { formatMessage } = useIntl();
  const [confidence, setConfidence] = useState(false);
  const [diarizationEnabled, setDiarizationEnabled] = useState(false);
  const diarizationApi = isDiarizationSegmentsEnabled(station);

  const [columnVisibility, setColumnVisibility] = useState({
    'text-with-confidence': confidence && !diarizationEnabled,
    text: false,
    'text-transcript': (!confidence && !diarizationEnabled) || (confidence && diarizationEnabled),
    'text-with-diarization': !confidence && diarizationEnabled,
    speakers: diarizationApi,
  });
  const { search } = useLocation();
  const queryParams = React.useMemo(() => new URLSearchParams(search), [search]);

  useEffect(() => {
    setColumnVisibility({
      'text-with-confidence': confidence && !diarizationEnabled,
      text: false,
      'text-transcript': (!confidence && !diarizationEnabled) || (confidence && diarizationEnabled),
      'text-with-diarization': !confidence && diarizationEnabled,
      speakers: diarizationApi,
    });
  }, [confidence, diarizationApi, diarizationEnabled]);

  const { speakers } = useContext(DiarizationContext);
  const transcriptionTimeParam = queryParams.get('transcriptionTime');

  const parsedData: TranscriptionType[] = useMemo(
    () => (data ? parseTranscription(data) : []),
    [data],
  );

  const formattedData = useMemo(
    () => (parsedData ? formatTranscript(parsedData, speakers) : []),
    [parsedData, speakers],
  );

  if (formattedData && formattedData.length > 0) {
    return (
      <>
        <LabelWrapper>
          {confidence && !diarizationEnabled ? <TrascriptionsLabel /> : <div />}
          <SwitchWrapper>
            {diarizationApi ? (
              <SwitchInput
                size={'sm'}
                checked={diarizationEnabled}
                onChange={(enabled) => setDiarizationEnabled(enabled)}
                label={formatMessage({
                  id: 'audit.transcriptionScreen.speakers',
                  defaultMessage: 'Speaker Labels',
                })}
              />
            ) : null}
            <SwitchInput
              size={'sm'}
              checked={confidence}
              onChange={(enabled) => setConfidence(enabled)}
              label={formatMessage({
                id: 'audit.transcriptionScreen.confidence',
                defaultMessage: 'Eficiencia',
              })}
            />
          </SwitchWrapper>
        </LabelWrapper>
        <MrtTable<FormattedTranscriptionType>
          enableSorting={false}
          enableGlobalFilter={true}
          muiTableBodyRowProps={({ row }) => {
            return {
              sx: {
                backgroundColor:
                  row.original.time === transcriptionTimeParam ? theme.aquaTranslucent : 'inherit',
                overflow: 'visible',
              },
            };
          }}
          filterFns={{
            transcriptFilterFn: filteredTranscript,
          }}
          globalFilterFn="transcriptFilterFn"
          columns={[
            {
              id: 'time',
              accessorKey: 'time',
              header: formatMessage({
                id: 'audit.props.time',
                defaultMessage: 'Hora',
              }),
              size: 20,
              minSize: 20,
              enableColumnFilter: false,
              enableGlobalFilter: false,
              Cell: ({ row: { original, index } }) => {
                return <LTime key={`time-${index}`} value={original.time} second={'numeric'} />;
              },
            },
            {
              id: 'text-with-confidence',
              accessorFn: (row) => row.wp_words,
              enableColumnFilter: false,
              header: formatMessage({
                id: 'audit.transcriptionScreen.sentence',
                defaultMessage: 'Texto',
              }),
              Cell: ({ row: { index }, renderedCellValue }) => {
                return <div key={`text-confidence-${index}`}>{renderedCellValue}</div>;
              },
            },
            {
              id: 'text-with-diarization',
              accessorFn: (row) => row.diarization_words,
              enableColumnFilter: false,
              header: formatMessage({
                id: 'audit.transcriptionScreen.sentence',
                defaultMessage: 'Texto',
              }),
              Cell: ({ row: { index }, renderedCellValue }) => {
                return <div key={`text-diarization-${index}`}>{renderedCellValue}</div>;
              },
            },
            {
              // This column is always hidden, it is used for filtering text values with the MRT default filter capabilities
              id: 'text',
              accessorFn: (row) => row.elastic_data.raw_content,
              enableColumnFilter: false,
              enableFilterMatchHighlighting: true,
              header: formatMessage({
                id: 'audit.transcriptionScreen.sentence',
                defaultMessage: 'Texto',
              }),
              filterFn: 'transcriptFilterFn',
              Cell: ({ row: { index }, renderedCellValue }) => (
                <div key={`text-${index}`}>{renderedCellValue}</div>
              ),
            },
            {
              id: 'text-transcript',
              accessorFn: (row) => row.elastic_data.raw_content,
              enableColumnFilter: false,
              enableFilterMatchHighlighting: true,
              header: formatMessage({
                id: 'audit.transcriptionScreen.sentence',
                defaultMessage: 'Texto',
              }),
              Cell: ({ row: { original, index }, renderedCellValue }) => (
                <TranscriptionTranscriptCell
                  original={original}
                  index={index}
                  renderedCellValue={renderedCellValue}
                />
              ),
            },
            {
              id: 'speakers',
              accessorFn: (row) => row.elastic_data?.diarization,
              enableColumnFilter: false,
              enableFilterMatchHighlighting: true,
              header: formatMessage({
                id: 'audit.transcriptionScreen.speakers',
                defaultMessage: 'Speaker Labels',
              }),
              size: 20,
              Cell: ({ row: { original, index } }) => (
                <TranscriptionSpeakersCell original={original} index={index} speakers={speakers} />
              ),
            },
          ]}
          data={formattedData}
          memoizeColumns={false}
          enableColumnFilters={false}
          enableDensityToggle={false}
          enableHiding={false}
          state={{
            columnVisibility,
          }}
        />
      </>
    );
  }

  return (
    <NullState className="p-7">
      {loading ? (
        <FormattedMessage
          id="audit.transcriptionScreen.loading"
          defaultMessage="Cargando transcripciones"
          values={{ count: 0 }}
        />
      ) : (
        <FormattedMessage
          id="audit.transcriptionScreen.noResults"
          defaultMessage="No transcriptions found"
          values={{ count: 0 }}
        />
      )}
    </NullState>
  );
});

export default TranscriptionsView;
